import React, { useEffect, useMemo, useState } from "react"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import TablePagination from "@mui/material/TablePagination"
import TextField from "@mui/material/TextField"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import Chip from "@mui/material/Chip"
import Box from "@mui/material/Box"
import { useSnackbar } from "notistack"

import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { verifyResponse } from "utils/httpRequest"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification, UseConfirmDialog } from "components/customHooks"
import { addProductGate, deleteProductGate, editProductGate, listProductGates } from "services/productGates"

import ProductGateDlg from "./ProductGateDlg"
import { ProductGatesWrapper } from "./ProductGates.style"

const normalizeCode = (code) => String(code || "").trim().toUpperCase()

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
]

const SORTABLE_COLUMNS = [
  { id: "name", label: "Name" },
  { id: "code", label: "Shortcode" },
  { id: "hubspotContentAccessId", label: "HubSpot Content Access ID" },
  { id: "checkoutUrl", label: "Checkout URL" },
  { id: "active", label: "Status" },
]

const compareValues = (a, b, sortBy) => {
  if (sortBy === "active") {
    const av = a?.active === false ? 0 : 1
    const bv = b?.active === false ? 0 : 1
    return av - bv
  }
  if (sortBy === "hubspotContentAccessId") {
    const av = Number(a?.hubspotContentAccessId) || 0
    const bv = Number(b?.hubspotContentAccessId) || 0
    return av - bv
  }
  const av = String(a?.[sortBy] ?? "").toLowerCase()
  const bv = String(b?.[sortBy] ?? "").toLowerCase()
  return av.localeCompare(bv)
}

export default function ProductGates() {
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState(["active", "inactive"])
  const [sortBy, setSortBy] = useState("name")
  const [sortOrder, setSortOrder] = useState("asc")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const canView =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])

  const canEdit =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Delete Product Gate",
    description: "Are you sure you want to delete this product gate?",
    agreeText: "Delete",
    disagreeText: "Cancel",
  })

  const load = async () => {
    setLoading(true)
    const resp = await listProductGates()
    setLoading(false)
    verifyResponse(resp)
    const arr = Array.isArray(resp?.data) ? resp.data : []
    setItems(arr)
  }

  useEffect(() => {
    load()
  }, [])

  const filteredSortedItems = useMemo(() => {
    const q = String(search || "").trim().toLowerCase()
    const includeActive = statusFilter.includes("active")
    const includeInactive = statusFilter.includes("inactive")

    let next = (items || []).filter((row) => {
      const isActive = row?.active !== false
      if (isActive && !includeActive) return false
      if (!isActive && !includeInactive) return false

      if (!q) return true
      const haystack = [
        row?.name,
        row?.code,
        row?.hubspotContentAccessId,
        row?.checkoutUrl,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ")
      return haystack.includes(q)
    })

    next = [...next].sort((a, b) => {
      const cmp = compareValues(a, b, sortBy)
      return sortOrder === "desc" ? -cmp : cmp
    })

    return next
  }, [items, search, statusFilter, sortBy, sortOrder])

  useEffect(() => {
    setPage(0)
  }, [search, statusFilter, sortBy, sortOrder, rowsPerPage])

  const pagedItems = useMemo(() => {
    const start = page * rowsPerPage
    return filteredSortedItems.slice(start, start + rowsPerPage)
  }, [filteredSortedItems, page, rowsPerPage])

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
      return
    }
    setSortBy(columnId)
    setSortOrder("asc")
  }

  const clearFilters = () => {
    setSearch("")
    setStatusFilter(["active", "inactive"])
    setSortBy("name")
    setSortOrder("asc")
    setPage(0)
  }

  const [openDlg, dlg] = ProductGateDlg({
    onSubmit: async (item, meta) => {
      const code = normalizeCode(item.code)

      // Uniqueness by code + hubspotContentAccessId (when provided)
      const conflict = items.find((p) => {
        const isSameRow = meta?.isEditMode && Number(p?.id) === Number(meta?.id)
        if (isSameRow) return false
        const sameCode = normalizeCode(p?.code) === code
        const sameHubspotId =
          item?.hubspotContentAccessId &&
          Number(p?.hubspotContentAccessId) === Number(item?.hubspotContentAccessId)
        return sameCode || sameHubspotId
      })
      if (conflict) {
        enqueueSnackbar("Duplicate product code or HubSpot Content Access ID", { variant: "warning" })
        return
      }

      const normalized = {
        id: item?.id ? Number(item.id) : undefined,
        name: String(item.name || "").trim(),
        code,
        hubspotContentAccessId: item.hubspotContentAccessId ? Number(item.hubspotContentAccessId) : null,
        checkoutUrl: item.checkoutUrl ? String(item.checkoutUrl).trim() : null,
        active: Boolean(item.active),
      }

      setLoading(true)
      try {
        let resp
        if (meta?.isEditMode) {
          resp = await editProductGate(normalized)
        } else {
          resp = await addProductGate(normalized)
        }
        verifyResponse(resp)
        enqueueSnackbar("Saved product gate", { variant: "success" })
        await load()
      } catch (e) {
        enqueueSnackbar("Failed to save product gate", { variant: "warning" })
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <ProductGatesWrapper>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Product Gates</h1>
          <p className="page-subtitle">
            Configure product shortcodes, HubSpot access IDs, and checkout
            destinations used by CMS and onboarding flows.
          </p>
        </div>
        <div className="actions">
          {canEdit && (
            <Button
              className="new-gate-btn"
              variant="contained"
              size="medium"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => openDlg()}
            >
              Add Product Gate
            </Button>
          )}
        </div>
      </PageHeading>

      <div className="page-surface">
        {!canView && (
          <div className="page-empty">
            You do not have permission to view product gates.
          </div>
        )}
        {canView && (
          <>
            <div className="filters-bar">
              <TextField
                className="search-field"
                size="small"
                label="Search"
                placeholder="Name, shortcode, HubSpot ID, URL…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FormControl className="status-filter" size="small">
                <InputLabel id="product-gate-status-filter-label">Status</InputLabel>
                <Select
                  labelId="product-gate-status-filter-label"
                  multiple
                  value={statusFilter}
                  onChange={(e) => {
                    const next = e.target.value
                    setStatusFilter(typeof next === "string" ? next.split(",") : next)
                  }}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => (
                    <Box className="status-chips">
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          size="small"
                          label={STATUS_OPTIONS.find((o) => o.value === value)?.label || value}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={statusFilter.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" onClick={clearFilters}>
                Clear
              </Button>
              <div className="filter-meta">
                {filteredSortedItems.length} of {items.length}
              </div>
            </div>

            <div className="table-shell">
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="product-gates table">
                  <TableHead>
                    <TableRow>
                      {SORTABLE_COLUMNS.map((col) => (
                        <TableCell
                          key={col.id}
                          sortDirection={sortBy === col.id ? sortOrder : false}
                        >
                          <TableSortLabel
                            active={sortBy === col.id}
                            direction={sortBy === col.id ? sortOrder : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedItems.length === 0 && (
                      <TableRow>
                        <TableCell className="table-empty" colSpan={6}>
                          {items.length === 0
                            ? "No product gates configured yet."
                            : "No product gates match your filters."}
                        </TableCell>
                      </TableRow>
                    )}
                    {pagedItems.map((row) => (
                      <TableRow key={`${row?.id || ""}-${row?.code || ""}`}>
                        <TableCell>{row?.name}</TableCell>
                        <TableCell>{row?.code}</TableCell>
                        <TableCell>
                          {row?.hubspotContentAccessId || <span className="muted">—</span>}
                        </TableCell>
                        <TableCell className="checkout-url">
                          {row?.checkoutUrl || <span className="muted">—</span>}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`status-chip ${
                              row?.active === false ? "inactive" : "active"
                            }`}
                          >
                            {row?.active === false ? "Inactive" : "Active"}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          {canEdit && (
                            <div className="table-actions">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => openDlg(row)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={() => {
                                  openConfirmDialog(async () => {
                                    setLoading(true)
                                    try {
                                      const resp = await deleteProductGate({ id: row?.id })
                                      verifyResponse(resp)
                                      enqueueSnackbar("Deleted product gate", { variant: "success" })
                                      await load()
                                    } catch (e) {
                                      enqueueSnackbar("Failed to delete product gate", {
                                        variant: "warning",
                                      })
                                    } finally {
                                      setLoading(false)
                                    }
                                  })
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredSortedItems.length}
                page={page}
                onPageChange={(_e, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
          </>
        )}
      </div>

      <LoadingScreen loading={loading} />
      {ConfirmDialog}
      {dlg}
    </ProductGatesWrapper>
  )
}
